import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { match, Pattern } from 'ts-pattern';

import { useFetchInvitations, useFetchReferralCodeInformation } from '~/api/auth';
import { FetchError, HttpBusinessConflictError, HttpNotFoundError, HttpTooManyRequestsError } from '~/api/errors';
import { Button, ButtonLink, ErrorMessage } from '~/components';
import { Field, Form, Input, Select } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';

import type { ReferralCodeFormFields } from './RegistrationReferralCodeForm.types';

import { useRegistrationContext } from '../Registration.context';
import {
  formStyle,
  inputStyle,
  inputWrapperStyle,
  referralCodeInvitedAtStyle,
  referralCodeOptionStyle,
} from './RegistrationReferralCodeForm.css';

export const RegistrationReferralCodeForm = () => {
  const mutation = useFetchReferralCodeInformation();

  const { data, mutate: fetchInvitations } = useFetchInvitations();
  const invitations = data ?? [];

  const { accountInformation, referralCodeInformation, setReferralCodeInformation, setShowReferralCodeForm } = useRegistrationContext();

  const { control, handleSubmit, setValue } = useForm<ReferralCodeFormFields>({
    defaultValues: {
      referralCode: referralCodeInformation?.code ?? '',
    },
  });

  const { formatDate } = useIntl();
  const { t } = useTranslation(['auth', 'common']);

  useEffect(() => {
    if (accountInformation.emailAddress !== '') fetchInvitations({ emailAddress: accountInformation.emailAddress });
  }, [accountInformation.emailAddress, fetchInvitations]);

  const hasSingleInvitation = invitations.length === 1;
  const hasMultipleInvitations = invitations.length > 1;

  const onSubmit = handleSubmit(({ referralCode }) => {
    // When empty, clear any applied code and close the form.
    if (!referralCode) {
      setReferralCodeInformation(null);
      setShowReferralCodeForm(false);
      return;
    }

    const invitation = invitations.find((invitation) => invitation.referralCode.code === referralCode) ?? null;

    if (invitation) {
      setReferralCodeInformation(invitation.referralCode);
      setShowReferralCodeForm(false);
      return;
    }

    mutation.mutate(referralCode, {
      onSuccess: (referralCodeInformation) => {
        setReferralCodeInformation(referralCodeInformation);
        setShowReferralCodeForm(false);
      },
    });
  });

  return (
    <Form className={formStyle} onSubmit={onSubmit}>
      <Controller
        control={control}
        name="referralCode"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { invalid, isDirty, isTouched } }) => (
          <>
            {hasMultipleInvitations && (
              // Both fields edit `referralCode`, so only the one holding the input carries the name.
              <Field.Root>
                <Field.Label optional>{t('auth:registrationPage.common.referralCode.form.referralCodeField.label')}</Field.Label>

                <Field.Description>{t('auth:registrationPage.common.referralCode.form.invitationsField.description')}</Field.Description>

                <Select.Root onValueChange={onChange} value={value}>
                  <Select.Button autoFocus>
                    {(code: string) => invitations.find((invitation) => invitation.referralCode.code === code)?.name ?? ''}
                  </Select.Button>

                  <Select.List>
                    {invitations.map((invitation) => (
                      <Select.Item key={invitation.referralCode.code} value={invitation.referralCode.code}>
                        <span className={referralCodeOptionStyle}>
                          <span>{invitation.name}</span>
                          <span className={referralCodeInvitedAtStyle}>
                            {t('auth:registrationPage.common.referralCode.form.invitationsField.invitedAt', {
                              date: formatDate(invitation.date),
                            })}
                          </span>
                        </span>
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Root>
              </Field.Root>
            )}

            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              {hasMultipleInvitations ? (
                <Field.Label srOnly>{t('auth:registrationPage.common.referralCode.form.referralCodeField.label')}</Field.Label>
              ) : (
                <Field.Label optional>{t('auth:registrationPage.common.referralCode.form.referralCodeField.label')}</Field.Label>
              )}

              {hasSingleInvitation && referralCodeInformation?.code !== invitations[0].referralCode.code && (
                <Field.Description>
                  <Trans
                    components={{
                      code: (
                        <ButtonLink
                          onClick={() => {
                            setValue('referralCode', invitations[0].referralCode.code);
                            onSubmit();
                          }}
                        />
                      ),
                    }}
                    i18nKey="auth:registrationPage.common.referralCode.form.referralCodeField.invitationDescription"
                    values={{ name: invitations[0].name, code: invitations[0].referralCode.code }}
                  />
                </Field.Description>
              )}

              <div className={inputWrapperStyle}>
                <Input
                  autoFocus={hasSingleInvitation}
                  className={inputStyle}
                  onBlur={onBlur}
                  onValueChange={onChange}
                  ref={ref}
                  value={value}
                />

                <Button emphasis="secondary" loading={mutation.isPending} type="submit">
                  {t('auth:registrationPage.common.referralCode.form.submitButton')}
                </Button>
              </div>
            </Field.Root>
          </>
        )}
      />

      {mutation.isError && (
        <ErrorMessage>
          {match(mutation.error)
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'ReferralCodeExpired',
              () => t('auth:registrationPage.common.referralCode.form.referralCodeField.errors.expired'),
            )
            .with(Pattern.instanceOf(HttpNotFoundError), () =>
              t('auth:registrationPage.common.referralCode.form.referralCodeField.errors.invalid'),
            )
            .with(Pattern.instanceOf(HttpTooManyRequestsError), () => t('common:errors.tooManyRequests'))
            .with(Pattern.instanceOf(FetchError), () => t('common:errors.fetchError'))
            .otherwise(() => t('auth:registrationPage.common.referralCode.form.referralCodeField.errors.generic'))}
        </ErrorMessage>
      )}
    </Form>
  );
};
